/* eslint-disable complexity */
import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import Empty from 'constants/empty';
import {
    Box, Text, Link, Button
} from 'components/ui';
import localeUtils from 'utils/LanguageLocale';
import ExtraProductDetailsUtils from 'utils/ExtraProductDetailsUtils';
import UserSubscriptionsSelector from 'selectors/user/userSubscriptions/userSubscriptionsSelector';
import SameDayAvailabilityStatusSelector from 'selectors/page/product/currentSku/actionFlags/sameDayAvailabilityStatusSelector';
import SameDayDeliveryMessageSelector from 'selectors/page/product/currentSku/sameDayDeliveryMessageSelector';
import PreferredZipCodeSelector from 'selectors/user/preferredZipCodeSelector';
import SDUBannerEN from 'components/ProductPage/DeliveryOptions/SDUBanner/locales/SDUBanner_en_US';
import SDUBannerFR from 'components/ProductPage/DeliveryOptions/SDUBanner/locales/SDUBanner_fr_CA';

const { preferredZipCodeSelector } = PreferredZipCodeSelector;
const { userSubscriptionsSelector } = UserSubscriptionsSelector;
const { sameDayDeliveryMessageSelector } = SameDayDeliveryMessageSelector;
const { sameDayAvailabilityStatusSelector } = SameDayAvailabilityStatusSelector;
const getText = localeUtils.getLocalisedDataFromResource(SDUBannerEN, SDUBannerFR);

const SDUBanner = ({ isSDUAddedToBasket, skuTrialEligibility, skuTrialPeriod, renderSDULandingPage }) => {
    const userSubscriptions = useSelector(userSubscriptionsSelector);
    const sameDayAvailabilityStatus = useSelector(sameDayAvailabilityStatusSelector);
    const zipCode = useSelector(preferredZipCodeSelector);
    const sameDayDeliveryMessage = useSelector(sameDayDeliveryMessageSelector);

    const { isSamedayUnlimitedEnabled } = Sephora.configurationSettings;
    const sduSubscriptions = userSubscriptions?.filter(subscription => subscription.type === 'SDU');
    const subscriptionStatus = sduSubscriptions[0]?.status;
    const isUserTrialEligible = sduSubscriptions[0]?.isTrialEligible && skuTrialEligibility;
    const isAnonymousUser = !sduSubscriptions[0];
    const isOutOfStock = ExtraProductDetailsUtils.isOutOfStock(sameDayAvailabilityStatus);
    const isBannerVisible = Boolean(zipCode && !isOutOfStock && sameDayDeliveryMessage);

    // Preserve the HOC's 4-way branch verbatim. Each branch returns a bundle of
    // locale strings + metadata, or null (equivalent to HOC returning {}).
    let bannerContent = null;

    if (subscriptionStatus === 'ACTIVE' || !isSamedayUnlimitedEnabled) {
        bannerContent = null;
    } else if (!isUserTrialEligible && isSDUAddedToBasket && !isAnonymousUser) {
        // SDU subscription in basket (not the trial)
        bannerContent = {
            title: getText('youGetFreeSameDayDelivery'),
            text: getText('yourBasketContains'),
            boldText: getText('sameDayUnlimited'),
            ending: getText('subscription'),
            linkText: getText('learnMore'),
            skuTrialPeriod: null,
            isBannerVisible
        };
    } else if ((isUserTrialEligible || isAnonymousUser) && isSDUAddedToBasket) {
        // Trial added to basket
        bannerContent = {
            title: getText('youGetFreeSameDayDelivery'),
            text: getText('yourBasketContains'),
            boldText: getText('free'),
            boldTextDayTrial: getText('dayTrial'),
            ending: getText('ofSameDayUnlimited'),
            linkText: getText('learnMore'),
            skuTrialPeriod,
            isBannerVisible
        };
    } else if ((isUserTrialEligible && !isSDUAddedToBasket) || isAnonymousUser) {
        // trial eligible, NOT in basket
        bannerContent = {
            title: getText('getFreeSameDayDelivery'),
            text: getText('startSavingWithA'),
            boldText: getText('free'),
            boldTextDayTrial: getText('dayTrial'),
            ending: getText('ofSephoraSDU'),
            linkText: getText('signUp'),
            tryNowForFreeText: getText('tryNowForFree'),
            skuTrialPeriod,
            isBannerVisible,
            showButtonCta: true
        };
    } else {
        // not eligible and NOT in basket
        bannerContent = {
            title: getText('getFreeSameDayDelivery'),
            text: getText('startSavingWithSephora'),
            boldText: getText('sameDayUnlimited'),
            linkText: getText('signUp'),
            skuTrialPeriod: null,
            isBannerVisible
        };
    }

    if (!bannerContent || !bannerContent.title || !bannerContent.isBannerVisible) {
        return null;
    }

    const {
        title,
        text,
        boldText,
        boldTextDayTrial,
        ending = Empty.String,
        linkText,
        showButtonCta,
        tryNowForFreeText,
        skuTrialPeriod: effectiveSkuTrialPeriod
    } = bannerContent;

    return (
        <Box
            paddingX={3}
            paddingY={2}
            borderRadius={2}
            borderWidth={1}
            borderColor='lightGray'
            backgroundColor='white'
        >
            <Text
                is='h2'
                color='red'
                fontWeight='bold'
                children={title}
            />
            <Text
                is='p'
                fontSize='sm'
            >
                {text}
                <strong>
                    {' '}
                    {boldText}
                    {effectiveSkuTrialPeriod ? ` ${effectiveSkuTrialPeriod}` : ending ? ' ' : ''}
                    {boldTextDayTrial}
                </strong>
                {ending}
                {ending ? ' ' : '. '}
                {showButtonCta && (
                    <div>
                        <Button
                            size='xs'
                            variant='secondary'
                            onClick={renderSDULandingPage}
                            children={tryNowForFreeText}
                            marginTop='8px'
                        />
                    </div>
                )}
                {!showButtonCta && (
                    <Link
                        color='blue'
                        underline={true}
                        onClick={renderSDULandingPage}
                    >
                        {linkText}
                    </Link>
                )}
            </Text>
        </Box>
    );
};

SDUBanner.propTypes = {
    isSDUAddedToBasket: PropTypes.bool,
    skuTrialEligibility: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    skuTrialPeriod: PropTypes.string,
    renderSDULandingPage: PropTypes.func
};

export default wrapFunctionalComponent(SDUBanner, 'SDUBanner');
